import React, { useState } from 'react';
import SupplyChainMap from './SupplyChainMap';
import FilterPanel from './FilterPanel';
import KpiCards from './KpiCards';
import SupplierDetailPanel from './SupplierDetailPanel';
import { SupplierData, mockSupplierData } from '../data/mockData';
import { FilterIcon, ZoomInIcon, ZoomOutIcon, LayersIcon } from 'lucide-react';
const Dashboard: React.FC = () => {
  const [selectedSupplier, setSelectedSupplier] = useState<SupplierData | null>(null);
  const [filters, setFilters] = useState({
    tier: 'all',
    category: 'all',
    region: 'all',
    status: 'all',
    searchQuery: '',
    startDate: '',
    endDate: '',
    hazardTypes: ['natural', 'port', 'political', 'infrastructure']
  });
  const [showFilters, setShowFilters] = useState(true);
  const [mapZoom, setMapZoom] = useState(2);
  const [activeMapLayer, setActiveMapLayer] = useState('default');
  const handleSupplierSelect = (supplier: SupplierData | null) => {
    setSelectedSupplier(supplier);
  };
  const handleFilterChange = (newFilters: any) => {
    setFilters({
      ...filters,
      ...newFilters
    });
  };
  const handleZoomIn = () => {
    setMapZoom(prev => Math.min(prev + 1, 18));
  };
  const handleZoomOut = () => {
    setMapZoom(prev => Math.max(prev - 1, 1));
  };
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };
  const toggleMapLayer = () => {
    setActiveMapLayer(activeMapLayer === 'default' ? 'satellite' : 'default');
  };
  return <div className="flex flex-1 h-[calc(100vh-64px)]" data-id="element-6">
      {/* Filter sidebar */}
      <div className={`bg-white border-r border-gray-200 transition-all duration-300 ${showFilters ? 'w-80' : 'w-0 overflow-hidden'}`} data-id="element-7">
        {showFilters && <FilterPanel filters={filters} onFilterChange={handleFilterChange} data-id="element-8" />}
      </div>
      {/* Main content */}
      <div className="flex flex-col flex-1 overflow-hidden" data-id="element-9">
        {/* KPI row */}
        <div className="bg-white p-4 border-b border-gray-200" data-id="element-10">
          <KpiCards data-id="element-11" />
        </div>
        {/* Map container */}
        <div className="relative flex-1 overflow-hidden" data-id="element-12">
          <SupplyChainMap suppliers={mockSupplierData} selectedSupplier={selectedSupplier} onSupplierSelect={handleSupplierSelect} filters={filters} zoom={mapZoom} data-id="element-13" />
          {/* Map controls */}
          <div className="absolute top-4 left-4 z-10 flex flex-col gap-2" data-id="element-14">
            <button onClick={toggleFilters} className="bg-white p-2 rounded-md shadow-md hover:bg-gray-100" title="Toggle filters" data-id="element-15">
              <FilterIcon size={20} className="text-[#005a9e]" data-id="element-16" />
            </button>
            <button onClick={handleZoomIn} className="bg-white p-2 rounded-md shadow-md hover:bg-gray-100" title="Zoom in" data-id="element-17">
              <ZoomInIcon size={20} className="text-[#005a9e]" data-id="element-18" />
            </button>
            <button onClick={handleZoomOut} className="bg-white p-2 rounded-md shadow-md hover:bg-gray-100" title="Zoom out" data-id="element-19">
              <ZoomOutIcon size={20} className="text-[#005a9e]" data-id="element-20" />
            </button>
            <button onClick={toggleMapLayer} className="bg-white p-2 rounded-md shadow-md hover:bg-gray-100" title="Map layers" data-id="element-21">
              <LayersIcon size={20} className="text-[#005a9e]" data-id="element-22" />
            </button>
          </div>
          {/* Map legend */}
          <div className="absolute bottom-4 right-4 z-10 bg-white p-3 rounded-md shadow-md" data-id="element-23">
            <h4 className="text-sm font-bold text-gray-700 mb-2" data-id="element-24">Legend</h4>
            <div className="space-y-2" data-id="element-25">
              <div className="flex items-center" data-id="element-26">
                <div className="w-4 h-4 rounded-full bg-[#4caf50] mr-2" data-id="element-27"></div>
                <span className="text-xs" data-id="element-28">On Schedule</span>
              </div>
              <div className="flex items-center" data-id="element-29">
                <div className="w-4 h-4 rounded-full bg-[#fbc02d] mr-2" data-id="element-30"></div>
                <span className="text-xs" data-id="element-31">Delayed</span>
              </div>
              <div className="flex items-center" data-id="element-32">
                <div className="w-4 h-4 rounded-full bg-[#e53935] mr-2" data-id="element-33"></div>
                <span className="text-xs" data-id="element-34">At Risk</span>
              </div>
              <div className="border-t border-gray-200 pt-2 mt-2" data-id="element-35">
                <div className="flex items-center" data-id="element-36">
                  <div className="w-6 h-6 rounded-full bg-[#005a9e] text-white flex items-center justify-center text-xs mr-2" data-id="element-37">
                    <span data-id="element-38">+</span>
                  </div>
                  <span className="text-xs" data-id="element-39">Supplier Cluster</span>
                </div>
              </div>
              {/* Hazard Legend */}
              <div className="border-t border-gray-200 pt-2 mt-2" data-id="element-40">
                <h5 className="text-xs font-medium mb-1" data-id="element-41">Hazards & Issues</h5>
                <div className="flex items-center" data-id="element-42">
                  <img src="https://cdn-icons-png.flaticon.com/512/4150/4150904.png" className="w-4 h-4 mr-2" data-id="element-43" />
                  <span className="text-xs" data-id="element-44">Natural Hazard</span>
                </div>
                <div className="flex items-center" data-id="element-45">
                  <img src="https://cdn-icons-png.flaticon.com/512/9703/9703236.png" className="w-4 h-4 mr-2" data-id="element-46" />
                  <span className="text-xs" data-id="element-47">Port Issue</span>
                </div>
                <div className="flex items-center" data-id="element-48">
                  <img src="https://cdn-icons-png.flaticon.com/512/3575/3575596.png" className="w-4 h-4 mr-2" data-id="element-49" />
                  <span className="text-xs" data-id="element-50">Political Issue</span>
                </div>
                <div className="flex items-center" data-id="element-51">
                  <img src="https://cdn-icons-png.flaticon.com/512/3028/3028331.png" className="w-4 h-4 mr-2" data-id="element-52" />
                  <span className="text-xs" data-id="element-53">Infrastructure Issue</span>
                </div>
              </div>
              {/* Severity Legend */}
              <div className="border-t border-gray-200 pt-2 mt-2" data-id="element-54">
                <h5 className="text-xs font-medium mb-1" data-id="element-55">Impact Severity</h5>
                <div className="flex items-center" data-id="element-56">
                  <div className="w-4 h-4 rounded-full bg-[#4caf50] opacity-30 mr-2" data-id="element-57"></div>
                  <span className="text-xs" data-id="element-58">Low</span>
                </div>
                <div className="flex items-center" data-id="element-59">
                  <div className="w-4 h-4 rounded-full bg-[#fbc02d] opacity-30 mr-2" data-id="element-60"></div>
                  <span className="text-xs" data-id="element-61">Medium</span>
                </div>
                <div className="flex items-center" data-id="element-62">
                  <div className="w-4 h-4 rounded-full bg-[#ff9800] opacity-30 mr-2" data-id="element-63"></div>
                  <span className="text-xs" data-id="element-64">High</span>
                </div>
                <div className="flex items-center" data-id="element-65">
                  <div className="w-4 h-4 rounded-full bg-[#e53935] opacity-30 mr-2" data-id="element-66"></div>
                  <span className="text-xs" data-id="element-67">Critical</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Detail panel */}
      {selectedSupplier && <div className="bg-white border-l border-gray-200 w-96 overflow-y-auto" data-id="element-68">
          <SupplierDetailPanel supplier={selectedSupplier} onClose={() => setSelectedSupplier(null)} data-id="element-69" />
        </div>}
    </div>;
};
export default Dashboard;